import { useState } from 'react';
import { Link } from 'react-router';

import type { TeamOverview } from '../api/types';
import { useProject } from '../projects/context';
import { scopeText } from './nextCall';
import { ScopeDialog } from './ScopeDialog';
import { TargetsDialog } from './TargetsDialog';

const n = new Intl.NumberFormat('de-DE');

// Tabellen der Steuerung (Gebiete, Makler, Telefonisten); jede Zahl führt zur passenden gefilterten Liste.
export function AreasTable({ areas }: { areas: TeamOverview['areas'] }) {
  const { project } = useProject();
  const base = `/p/${project.key}`;
  if (areas.length === 0) return <p className="muted">Noch keine Gebiete angelegt.</p>;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Gebiet</th>
            <th>Makler</th>
            <th className="num">Leads</th>
            <th className="num">noch nicht angerufen</th>
            <th className="num">Termine (7 Tage)</th>
            <th className="num">offene Rückfragen</th>
          </tr>
        </thead>
        <tbody>
          {areas.map((a) => (
            <tr key={a.area_id}>
              <td>
                <Link to={`${base}/gebiete/${a.area_id}`}>{a.name}</Link>
              </td>
              <td>
                {a.field_sales_user_id ? (
                  <Link to={`${base}/makler/${a.field_sales_user_id}`}>{a.field_sales_name}</Link>
                ) : (
                  <span className="badge badge-warn">kein Makler</span>
                )}
              </td>
              <td className="num">
                <Link to={`${base}/leads?area_id=${a.area_id}`}>{n.format(a.leads)}</Link>
              </td>
              <td className="num">
                <Link to={`${base}/leads?area_id=${a.area_id}&status=NEW`}>{n.format(a.new_leads)}</Link>
              </td>
              <td className="num">
                <Link to={`${base}/termine?ansicht=kalender&area=${a.area_id}`}>{a.appointments_next_7_days}</Link>
              </td>
              <td className="num">
                <Link to={`${base}/posteingang?ansicht=rueckfragen&gebiet=${a.area_id}`}>{a.open_tickets}</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function MaklerTable({ fieldSales }: { fieldSales: TeamOverview['field_sales'] }) {
  const { project } = useProject();
  const base = `/p/${project.key}`;
  if (fieldSales.length === 0) return <p className="muted">Noch keine Makler im Projekt.</p>;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Makler</th>
            <th>Gebiete</th>
            <th className="num">Leads</th>
            <th className="num">Termine heute</th>
            <th className="num">7 Tage</th>
            <th className="num">letzte 30 Tage</th>
            <th className="num">offene Rückfragen</th>
            <th>Outlook</th>
          </tr>
        </thead>
        <tbody>
          {fieldSales.map((m) => (
            <tr key={m.user_id}>
              <td>
                <Link to={`${base}/makler/${m.user_id}`}>{m.name}</Link>
              </td>
              <td>{m.areas.join(', ') || <span className="badge badge-warn">kein Gebiet</span>}</td>
              <td className="num">
                <Link to={`${base}/leads?assigned_field_sales_user_id=${m.user_id}`}>{n.format(m.leads)}</Link>
              </td>
              <td className="num">{m.appointments_today}</td>
              <td className="num">
                <Link to={`${base}/termine?ansicht=kalender&user=${m.user_id}`}>{m.appointments_next_7_days}</Link>
              </td>
              <td className="num">{m.appointments_last_30_days}</td>
              <td className="num">
                <Link to={`${base}/posteingang?ansicht=rueckfragen&makler=${m.user_id}`}>{m.open_tickets}</Link>
              </td>
              <td>{m.outlook_connected ? <span className="badge badge-ok">verbunden</span> : <span className="badge badge-muted">nicht verbunden</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// progress: „12 / 60“ – mit Ziel, sonst nur die Zahl.
const progress = (value: number, target?: number) => (target ? `${value} / ${target}` : String(value));

type TelesalesRow = TeamOverview['telesales'][number];

export function TelesalesTable({ telesales, fieldSales = [] }: { telesales: TeamOverview['telesales']; fieldSales?: { user_id: string; name: string }[] }) {
  const { can } = useProject();
  const [editing, setEditing] = useState<TelesalesRow>();
  const [scoping, setScoping] = useState<TelesalesRow>();
  if (telesales.length === 0) return <p className="muted">Noch keine Telefonisten im Projekt.</p>;
  const manage = can('project.manage');
  const assign = can('area.assign_user');
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Telefonist</th>
            <th>Telefoniert für</th>
            <th className="num">Anrufe heute</th>
            <th className="num">Gespräche heute</th>
            <th className="num">Termine heute</th>
            <th className="num">Termine 7 Tage</th>
            <th className="num">offene Wiedervorlagen</th>
            {manage && <th>Tagesziel</th>}
          </tr>
        </thead>
        <tbody>
          {telesales.map((t) => (
            <tr key={t.user_id}>
              <td>{t.name}</td>
              <td>
                <span className={t.scope.field_sales.length || t.scope.areas.length ? '' : 'muted'}>{scopeText(t.scope)}</span>
                {assign && (
                  <button type="button" className="btn btn-small btn-inline" onClick={() => setScoping(t)} aria-label={`Zuständigkeit von ${t.name}`}>
                    Ändern
                  </button>
                )}
              </td>
              <td className="num">{progress(t.calls_today, t.targets?.calls || undefined)}</td>
              <td className="num">{progress(t.conversations_today, t.targets?.conversations || undefined)}</td>
              <td className="num">{progress(t.appointments_today, t.targets?.appointments || undefined)}</td>
              <td className="num">{t.appointments_week}</td>
              <td className="num">{t.open_follow_ups}</td>
              {manage && (
                <td>
                  <button type="button" className="btn btn-small" onClick={() => setEditing(t)} aria-label={`Tagesziele für ${t.name}`}>
                    {t.targets ? 'Ändern' : 'Festlegen'}
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {editing && <TargetsDialog userId={editing.user_id} name={editing.name} current={editing.targets} onClose={() => setEditing(undefined)} />}
      {scoping && (
        <ScopeDialog userId={scoping.user_id} name={scoping.name} current={scoping.scope} fieldSales={fieldSales} onClose={() => setScoping(undefined)} />
      )}
    </div>
  );
}
